import { DateTime } from 'luxon';
import { getShiftCodeAt } from '../../../../shared/services/shifts/shifts.ts';
import { ShiftSlot } from '../../../../shared/types/shiftTypes.ts';
import { sumOf } from '../../../../shared/utils/arrayUtils.ts';
import { OeeRow } from '../../../types/sharedTypeImpl.ts';
import { Bar, BarGraphData, BarGroup, Label } from '../barGraphData.ts';
import { PeriodOption, ReportSettings } from '../report-settings/reportSettings.ts';
import { getShiftSlotColor } from '../reportGraphColors.ts';


interface OeeRow2 extends OeeRow {
    isOnWeekend: boolean;
}


export function prepareScrapsRatios(
    oeeData: OeeRow[],
    reportSettings: ReportSettings,
): BarGraphData {
    const oeeDataTransformed = transformInputs(oeeData);
    const oeeDataFiltered = filterInputs(oeeDataTransformed, reportSettings);
    const barGroups = reportSettings.periodsInRange.map(period => getBarGroup(oeeDataFiltered, period, reportSettings));

    const values = barGroups.flatMap(barGroup =>
        barGroup.bars.flatMap(bar =>
            bar.sections.flatMap(section => section.value)
        )
    );
    const maxValue = Math.max(...values) ?? 0;

    return {
        barGroups,
        maxValue,
    };
}

function transformInputs(oeeData: OeeRow[]): OeeRow2[] {
    return oeeData.map(it => ({
        ...it,
        isOnWeekend: it.shiftCode === 'W',
        shiftCode: getShiftCodeAt(it.start, false),//Override shift code to convert W shifts to A/B/N
    }));
}

function filterInputs(oeeData: OeeRow2[], reportSettings: ReportSettings) {
    return oeeData
        .filter(it => reportSettings.visibleShifts.includes(it.shiftSlot))
        .filter(it => it.isOnWeekend && reportSettings.weekends || !it.isOnWeekend && reportSettings.workdays)
        .filter(it => it.start >= reportSettings.first.start && it.end <= reportSettings.last.end);
}

function getBarGroup(
    oeeData: OeeRow[],
    period: PeriodOption,
    reportSettings: ReportSettings,
): BarGroup {
    const oeePeriod = oeeData.filter(it => it.start >= period.start && it.start < period.end);// Filter day by shift start
    const resolutionIsWeekly = reportSettings.resolution === 'Weeks';

    const bars = [];
    if (reportSettings.shiftsDisplay === 'Separated') {
        const oeeShifts = splitABShifts(oeePeriod);
        const firstShiftA = isFirstShiftA(period, reportSettings);

        const aShiftBar = getBar(oeeShifts[0], 'A', resolutionIsWeekly ? (firstShiftA ? 'morning' : 'evening') : null);
        const bShiftBar = getBar(oeeShifts[1], 'B', resolutionIsWeekly ? (firstShiftA ? 'evening' : 'morning') : null);
        const nShiftBar = getBar(oeeShifts[2], 'N', resolutionIsWeekly ? 'night' : null);
        bars.push(firstShiftA ? aShiftBar : bShiftBar);
        bars.push(firstShiftA ? bShiftBar : aShiftBar);
        if (reportSettings.visibleShifts.includes('night')) {
            bars.push(nShiftBar);
        }
    } else {
        bars.push(getBar(oeePeriod, null, null));
    }

    return {
        periodLabel: period.periodLabel,
        bars,
    };
}

function splitABShifts(oeeData: OeeRow[]): OeeRow[][] {
    const delaysInShiftA = [];
    const delaysInShiftB = [];
    const rowsInShiftN = [];
    for (const oeeRow of oeeData) {
        if (oeeRow.shiftCode === 'A') {
            delaysInShiftA.push(oeeRow);
        } else if (oeeRow.shiftCode === 'B') {
            delaysInShiftB.push(oeeRow);
        } else if (oeeRow.shiftCode === 'N') {
            rowsInShiftN.push(oeeRow);
        }
    }
    return [delaysInShiftA, delaysInShiftB, rowsInShiftN];
}

function getBar(oeeData: OeeRow[], shiftLabel: string | null, shiftSlot: ShiftSlot | null): Bar {
    const scrapsValue = getScrapsValue(oeeData) ?? 0;

    const barLabels: Label[] = shiftLabel
        ? [{ position: 'above-bottom', text: shiftLabel }]
        : [];

    return {
        sections: [{
            value: scrapsValue,
            color: getShiftSlotColor(shiftSlot),
            labels: [{ position: 'below-top', text: scrapsValue.toFixed(2) }],
        }],
        labels: barLabels,
    }
}

function getScrapsValue(oeeData: OeeRow[]): number | null {
    // Calc average quality weighted by seconds of production. Then get the scraps value as 1 - quality.
    // Note that we are ignoring performance. We aren't calculating it with accuracy anyway, so it's noise.
    const totalSum = sumOf(oeeData, it => it.productionTime);
    const qualitySum = sumOf(oeeData, it => it.productionTime * (it.quality ?? 0));

    return (totalSum && qualitySum)
        ? 1 - qualitySum / totalSum //1 - weighted average quality
        : 0;
}

function isFirstShiftA(period: PeriodOption, reportSettings: ReportSettings): boolean {
    const morningShiftTime = DateTime.fromJSDate(period.start).set({ hour: 10, minute: 0, second: 0, millisecond: 0 }); // Middle of morning shift
    const morningShiftCode = getShiftCodeAt(morningShiftTime);
    return morningShiftCode === 'A';
}
